import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {AutomatedEmailTable} from 'shells/automated-email-table/automated-email-table';

import type {EmailAutomationField} from 'nutshell-graphql-types';
import type {
    GetEmailAutomationsReadyToSend as GetEmailAutomationsReadyToSendQuery,
    GetEmailAutomationsReadyToSendVariables,
    EmailAutomationStatusManualStart as EmailAutomationStatusManualStartMutation,
    EmailAutomationStatusManualStartVariables,
    EmailAutomationStatusStatus as EmailAutomationStatus,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {AutomationListWrapper} from './automation-list-wrapper';
import {convertEdgesToTableData} from './convert-edges-to-table-data';
import START_EMAIL from './graphql/mutations/email-automation-status-manual-start.graphql';
import GET_READY_TO_SEND from './graphql/queries/get-email-automations-ready-to-send.graphql';

const LIMIT = 25;
const VALID_STATUSES: EmailAutomationStatus[] = ['READY_TO_SEND'];
const MANUALLY_SENT_STATUS: EmailAutomationStatus = 'QUEUED_MANUALLY';
const STATUS = 'READY_TO_SEND';

class ReadyToSendQuery extends Query<
    GetEmailAutomationsReadyToSendQuery,
    GetEmailAutomationsReadyToSendVariables
> {}

class StartEmailMutation extends Mutation<
    EmailAutomationStatusManualStartMutation,
    EmailAutomationStatusManualStartVariables
> {}

type GraphqlProps = {
    onCancel: (id: string, currentEmailAutomationStatus?: any) => Promise<any>;
    onUpdateStatusField: (
        emailAutomationStatus: any,
        field: EmailAutomationField,
        newValue?: string | null | undefined
    ) => Promise<any>;
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

type Props = GraphqlProps & ContextRouter;

/**
 * This annoying layer just gets the render props from AutomationListWrapper so that
 * we can use them as `props` in the actual component to do things like set up hooks and effects.
 */
function AutomationsReadyToSendPlusCancelAndModified(props: ContextRouter) {
    return (
        <AutomationListWrapper
            query={GET_READY_TO_SEND}
            fetchLimit={LIMIT}
            validStatuses={VALID_STATUSES}
            cacheKey='emailAutomationStatusesReadyToSend'
        >
            {(wrapperProps) => <AutomationsReadyToSendComponent {...props} {...wrapperProps} />}
        </AutomationListWrapper>
    );
}

function AutomationsReadyToSendComponent(props: Props) {
    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_AUTOMATIONS_READY_TO_SEND_VIEWED);
    }, []);

    const {onCancel, onUpdateStatusField} = props;

    return (
        <StartEmailMutation mutation={START_EMAIL}>
            {(startEmail) => (
                <ReadyToSendQuery
                    query={GET_READY_TO_SEND}
                    variables={{limit: LIMIT}}
                    notifyOnNetworkStatusChange={true}
                    // @ts-expect-error
                    fetchPolicy='cache-and-network'
                >
                    {({data, loading, fetchMore, error}) => {
                        const {endCursor, hasNextPage} =
                            data && data.emailAutomationStatusesReadyToSend
                                ? data.emailAutomationStatusesReadyToSend.pageInfo
                                : {};

                        const edges =
                            data && data.emailAutomationStatusesReadyToSend
                                ? data.emailAutomationStatusesReadyToSend.edges
                                : [];
                        const tableData = convertEdgesToTableData(
                            edges.filter((edge) => VALID_STATUSES.includes(edge.node.status))
                        );

                        return (
                            <AutomatedEmailTable
                                status={STATUS}
                                data={tableData}
                                isLoading={loading}
                                // @ts-expect-error
                                errMessage={safelyGetGraphQLErrorMessage(error)}
                                hasNextPage={hasNextPage}
                                history={props.history}
                                hasAutomatedEmailSequences={props.hasAutomatedEmailSequences}
                                hasConfiguredAutomations={props.hasConfiguredAutomations}
                                loadMore={() =>
                                    fetchMore({
                                        variables: {
                                            cursor: endCursor,
                                            limit: LIMIT,
                                        },
                                        updateQuery: (prev, {fetchMoreResult}) => {
                                            if (!fetchMoreResult) return prev;
                                            const prevData =
                                                prev.emailAutomationStatusesReadyToSend;
                                            const newData =
                                                fetchMoreResult.emailAutomationStatusesReadyToSend;

                                            return {
                                                ...prev,
                                                emailAutomationStatusesReadyToSend: {
                                                    ...prevData,
                                                    pageInfo: newData.pageInfo,
                                                    edges: [...prevData.edges, ...newData.edges],
                                                },
                                            };
                                        },
                                    })
                                }
                                onEmailAutomationStatusCancel={(emailAutomationStatusId) => {
                                    const currentEmailAutomationStatus = edges.find(
                                        (edge) => edge.node.id === emailAutomationStatusId
                                    );

                                    return onCancel(
                                        emailAutomationStatusId,
                                        currentEmailAutomationStatus
                                    );
                                }}
                                onUpdateField={(emailAutomationStatusId, field, newValue) => {
                                    const emailAutomationStatus = edges.find(
                                        (edge) => edge.node.id === emailAutomationStatusId
                                    );

                                    return onUpdateStatusField(
                                        emailAutomationStatus,
                                        field,
                                        newValue
                                    );
                                }}
                                onEmailAutomationStatusStart={(emailAutomationStatusId) => {
                                    const currentEmailAutomationStatus = edges.find(
                                        (edge) => edge.node.id === emailAutomationStatusId
                                    );

                                    const optimisticResponse = currentEmailAutomationStatus
                                        ? {
                                              __typename: 'Mutation',
                                              emailAutomationStatusManualStart: {
                                                  __typename:
                                                      'EmailAutomationStatusManualStartPayload',
                                                  emailAutomationStatus: {
                                                      ...currentEmailAutomationStatus.node,
                                                      status: MANUALLY_SENT_STATUS,
                                                  },
                                              },
                                          }
                                        : undefined;

                                    analyticsClient.track(
                                        analyticsClient.EVENTS
                                            .TRACK_AUTOMATION_STATUS_STARTED_FROM_LIST
                                    );

                                    return startEmail({
                                        variables: {
                                            input: {
                                                id: emailAutomationStatusId,
                                            },
                                        },
                                        // @ts-expect-error
                                        optimisticResponse,
                                    });
                                }}
                            />
                        );
                    }}
                </ReadyToSendQuery>
            )}
        </StartEmailMutation>
    );
}

// @ts-expect-error
export const AutomationsReadyToSend = withRouter<Record<any, any>>(
    AutomationsReadyToSendPlusCancelAndModified
);
